<template>
  <div class="auth-page">
    <b-container class="px-0">
      <b-row>
        <b-col v-if="isEcp && step === 1">
          <h1>Bienvenue sur l'offre Orange Cyber Protection</h1>
          <Alert v-if="isBlocked" :title="isBlockedMsg" type="danger" />
          <h4 class="mb-0">Pour plus de sécurité, merci de renseigner votre identifiant :</h4>
          <div class="mb-2">Il s'agit de l'identifiant que vous avez choisi lors de la souscription à l'offre Cyber Protection.</div>
          <Alert type="info" title="Si vous avez oublié votre identifiant, vous pouvez le réinitialiser" button="Réinitialiser vos identifiants" :action="() => $router.push('/configuration-infos')"/>
          <StepIdentifier
            :contact="contact"
            :type="type"
            :isLoading="isLoading"
            :errorLogin="errorLogin"
            :errorLoginMsg="errorLoginMsg"
            :warningLogin="warningLogin"
            :translations="translations"
            @update:contact="contact = $event"
            @update:phoneResults="phoneResults = $event"
            @submit="checkId"
          />
        </b-col>
        <b-col v-else class="col-xs-12 col-lg-10 main">
          <Link v-if="step !== 1" back :action="goBack" text="Retour" />
          <h1 v-if="step !== 3">Pour vous connecter</h1>
          <h1 v-else>Configurez vos questions secrètes</h1>
          <Alert v-if="step === 1" type="info" title="C'est votre première connexion ?" subtitle="Finalisez la création de votre compte en saisissant l'adresse e-mail ou le numéro de mobile communiqué lors de votre souscription."/>
          <Alert v-if="isBlocked" :title="isBlockedMsg" type="danger" />

          <StepIdentifier
            v-if="step === 1"
            :contact="contact"
            :type="type"
            :isLoading="isLoading"
            :errorLogin="errorLogin"
            :errorLoginMsg="errorLoginMsg"
            :warningLogin="warningLogin"
            :translations="translations"
            @update:contact="contact = $event"
            @update:phoneResults="phoneResults = $event"
            @submit="checkId"
          />

          <StepOtp
            v-else-if="step === 2"
            :otp="otp"
            :hiddenContact="hiddenContact"
            :deliveryType="getTypeByReverseAuth"
            :countryNotEnabled="country_not_enabled"
            :isLoading="isLoading"
            :isBlocked="isBlocked"
            :isCountdown="isCountdown"
            :errorOtp="errorOtp"
            :errorOtpMsg="errorOtpMsg"
            @complete="completeOtp"
            @validate="validateOtp"
            @resend="resendOtp"
            @countdownEnded="isCountdown = false"
          />

          <div v-else-if="step === 3">
            <StepSetupQuestions
              :question1="question1"
              :response1="response1"
              :question2="question2"
              :response2="response2"
              :question3="question3"
              :response3="response3"
              :questions1="options.questions1"
              :questions2="options.questions2"
              :questions3="options.questions3"
              :isLoading="isLoading"
              :isBlocked="isBlocked"
              @update:question1="onQuestion1Change"
              @update:response1="response1 = $event"
              @update:question2="onQuestion2Change"
              @update:response2="response2 = $event"
              @update:question3="question3 = $event"
              @update:response3="response3 = $event"
              @submit="validateQuestions"
            />
          </div>

          <StepVerifyQuestions
            v-else-if="step === 4"
            :q1="verifyQ1"
            :q2="verifyQ2"
            :verifyR1="verifyR1"
            :verifyR2="verifyR2"
            :allQuestions="options.questions1"
            :isLoading="isLoading"
            :isBlocked="isBlocked"
            :errorResponse1="errorResponse1"
            :errorResponse2="errorResponse2"
            @update:verifyR1="verifyR1 = $event"
            @update:verifyR2="verifyR2 = $event"
            @submit="verifyQuestions"
          />
        </b-col>
      </b-row>
    </b-container>
  </div>
</template>

<script>
import logger from '@/utils/func/logger'
import Alert from '@/components/Alert/Alert'
import Link from '@/components/Link/Link'
import countries from '@/utils/countries.json'
import StepIdentifier from './steps/StepIdentifier'
import StepOtp from './steps/StepOtp'
import StepSetupQuestions from './steps/StepSetupQuestions'
import StepVerifyQuestions from './steps/StepVerifyQuestions'

export default {
  name: 'PasswordlessLoginPage',
  components: { Alert, Link, StepIdentifier, StepOtp, StepSetupQuestions, StepVerifyQuestions },
  data () {
    return {
      isEcp: false,
      charRegex: /[a-zA-Z]{1,}/,
      mailRegex: /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/, //eslint-disable-line no-useless-escape
      isLoading: false,
      isCountdown: false,
      isBlocked: false,
      isBlockedMsg: '',
      isReverseAuth: null,
      type: 'email',
      otp: '',
      step: 1,
      verifyQ1: 0,
      verifyQ2: 0,
      verifyR1: '',
      verifyR2: '',
      errorResponse1: false,
      errorResponse2: false,
      question1: 0,
      question2: 0,
      question3: 0,
      response1: '',
      response2: '',
      response3: '',
      contact: '',
      hiddenContact: '',
      phoneResults: {},
      errorLogin: false,
      errorLoginMsg: '',
      warningLogin: false,
      errorOtp: false,
      errorOtpMsg: '',
      errorResponse: false,
      errorResponseMsg: '',
      options: {
        questions1: [],
        questions2: [],
        questions3: [],
      },
      translations: {
        countrySelectorLabel: 'Code pays',
        countrySelectorError: 'Choisir un pays',
        phoneNumberLabel: 'Numéro de téléphone',
        example: 'Exemple :',
        countrySelectorSearchPlaceholder: 'Rechercher un pays',
      },
      countries,
      country_not_enabled: false,
    }
  },
  computed: {
    getTypeByReverseAuth () {
      return (this.isReverseAuth && this.type === 'email') || (!this.isReverseAuth && this.type === 'phone') ? 'phone' : 'email'
    },
  },
  watch: {
    contact (n) {
      this.errorLogin = false
      this.errorLoginMsg = ''
      this.warningLogin = false
      if (!n || Array.from(n)[0] !== '+') {
        this.type = 'email'
      }
    },
  },
  mounted () {
    this.isEcp = !!this.$route.query.ecptoken
    if (!this.isEcp) {
      localStorage.removeItem('ecpToken')
    }
    if (this.$route.query.ecptoken) {
      this.$store.dispatch('auth/getUserInfos', { token: this.$route.query.ecptoken })
        .then((res) => {
          logger.log('Success auth/getUserInfos', res)
          this.contact = res.contact
        })
        .catch((err) => logger.error('Err auth/getUserInfos', err))
    }
    this.$store.dispatch('auth/getSecretQuestions')
      .then((res) => { this.options.questions1 = res.data })
      .catch((err) => logger.error('Err auth/getSecretQuestions', err))
    this.$store.dispatch('auth/getReverseAuth')
      .then((res) => { this.isReverseAuth = res.isReverseAuth })
      .catch((err) => logger.error('Err auth/getReverseAuth', err))
  },
  methods: {
    onQuestion1Change (val) {
      this.question1 = val
      this.options.questions2 = this.options.questions1.filter(q => q.id !== val)
      this.question2 = 0
    },
    onQuestion2Change (val) {
      this.question2 = val
      this.options.questions3 = this.options.questions2.filter(q => q.id !== val)
      this.question3 = 0
    },
    checkId () {
      this.isBlocked = false
      this.isBlockedMsg = ''
      this.errorLogin = false
      this.errorLoginMsg = ''
      this.warningLogin = false

      let contact = this.contact
      let type = 'email'

      if (contact.length === 0) {
        this.errorLoginMsg = 'Veuillez remplir ce champ.'
        this.errorLogin = true
        return
      }

      if (/^[0-9]*$/.test(contact)) {
        type = 'phone'
        contact = `+33${contact.slice(-contact.length + 1)}`
      } else if (Array.from(contact)[0] === '+') {
        type = 'phone'
      }

      contact = contact.replace(/ /, '')

      if (type === 'email' && !this.mailRegex.test(contact)) {
        this.errorLoginMsg = "Cette adresse mail n'est pas valide. Vérifiez votre saisie."
        this.errorLogin = true
        return
      }

      if (type === 'phone' && ![12, 13, 14].includes(contact.length)) {
        this.type = 'phone'
        this.contact = contact
        this.errorLogin = true
        this.errorLoginMsg = "Ce numéro de téléphone n'est pas valide. Vérifiez votre saisie."
        return
      }

      this.isLoading = true
      this.errorLogin = false
      this.errorLoginMsg = ''

      const data = {
        contact,
        type,
        forgot: false,
        isEcp: this.isEcp,
        ecpToken: this.isEcp ? localStorage.getItem('ecpToken') : null,
      }

      this.$store.dispatch('auth/checkUserExists', data)
        .then((res) => {
          logger.log('Success auth/checkUserExists', res)
          this.isLoading = false
          if (res.success) {
            if (type === 'phone' && res.country_not_enabled) {
              this.type = 'email'
              this.contact = res.email
            } else {
              this.type = type
              this.contact = contact
            }
            this.country_not_enabled = res.country_not_enabled
            this.step = 2
            this.hiddenContact = res.contact
            this.isCountdown = true
          } else {
            if (res.isBlocked) {
              this.isBlocked = true
              this.isBlockedMsg = `Votre compte est temporairement bloqué. Veuillez réessayer dans ${res.timer} minute${res.timer > 1 ? 's' : ''}.`
              window.scrollTo(0, 0)
            } else if (res.type && res.type === 'warn') {
              this.warningLogin = true
            } else {
              this.errorLogin = true
              this.errorLoginMsg = res.message
            }
          }
        })
        .catch((err) => {
          logger.error('Err auth/checkUserExists', err)
          this.isLoading = false
        })
    },
    resendOtp () {
      this.isLoading = true
      this.$store.dispatch('auth/resendOtp', {
        contact: this.contact,
        type: this.type,
        forgot: false,
        isEcp: this.isEcp,
        ecpToken: this.isEcp ? localStorage.getItem('ecpToken') : null,
      })
        .then((res) => {
          logger.log('Success auth/resendOtp', res)
          this.isLoading = false
          this.isCountdown = true
          this.errorOtp = false
          this.errorOtpMsg = ''
        })
        .catch((err) => {
          logger.error('Err auth/resendOtp', err)
          this.isLoading = false
        })
    },
    completeOtp (nextOtp) {
      if (this.isBlocked) return
      if (this.otp.length < 6) {
        this.otp = nextOtp
        this.isLoading = true
        this.validateOtp()
      } else {
        const prev = this.otp
        this.otp = nextOtp
        if (prev.slice(-1) !== nextOtp.slice(-1)) {
          this.isLoading = true
          this.validateOtp()
        }
      }
    },
    validateOtp () {
      this.isLoading = true
      if (this.otp.trim().length !== 6) {
        this.isLoading = false
        this.errorOtp = true
        this.errorOtpMsg = 'Veuillez saisir le code à 6 chiffres.'
        return
      }

      this.$store.dispatch('auth/validateOtp', {
        contact: this.contact,
        otp: this.otp,
        type: this.type,
        forgot: false,
        isEcp: this.isEcp,
        ecpToken: this.isEcp ? localStorage.getItem('ecpToken') : null,
      })
        .then((res) => {
          logger.log('Success auth/validateOtp', res)
          if (res.success) {
            this.errorOtp = false
            this.errorOtpMsg = ''
            if (res.isQuestionsSecretes) {
              this.isLoading = false
              this.step = 3
            } else if (res.wasBlocked) {
              this.isLoading = false
              this.isBlocked = false
              this.step = 4
              this.verifyQ1 = res.questions[0]
              this.verifyQ2 = res.questions[1]
            } else {
              this.isLoading = true
              this.authenticate()
            }
          } else {
            this.isLoading = false
            if (res.isBlocked) {
              this.isBlocked = true
              this.isBlockedMsg = 'Votre compte est temporairement bloqué. Veuillez réessayer dans 30 minutes.'
              this.errorOtp = false
              this.errorOtpMsg = ''
              window.scrollTo(0, 0)
            } else {
              this.errorOtp = true
              this.errorOtpMsg = res.message
            }
          }
        })
        .catch((err) => {
          logger.error('Err auth/validateOtp', err)
          this.isLoading = false
          this.errorOtp = true
          this.errorOtpMsg = 'Une erreur est survenue. Veuillez réessayer.'
        })
    },
    validateQuestions () {
      this.isLoading = true
      this.$store.dispatch('auth/storeSecretQuestions', {
        contact: this.contact,
        type: this.type,
        q1: this.question1,
        r1: this.response1,
        q2: this.question2,
        r2: this.response2,
        q3: this.question3,
        r3: this.response3,
        isEcp: this.isEcp,
        ecpToken: this.isEcp ? localStorage.getItem('ecpToken') : null,
      })
        .then((res) => {
          logger.log('Success auth/storeSecretQuestions', res)
          this.isLoading = false
          if (res.success) {
            this.authenticate()
          }
        })
        .catch((err) => {
          logger.error('Err auth/storeSecretQuestions', err)
          this.isLoading = false
        })
    },
    verifyQuestions () {
      if (this.isBlocked) return
      this.isLoading = true
      this.$store.dispatch('auth/validateSecretQuestions', {
        contact: this.contact,
        type: this.type,
        forgot: false,
        q1: this.verifyQ1,
        r1: this.verifyR1,
        q2: this.verifyQ2,
        r2: this.verifyR2,
        isEcp: this.isEcp,
        ecpToken: this.isEcp ? localStorage.getItem('ecpToken') : null,
      })
        .then((res) => {
          logger.log('Success auth/validateSecretQuestions', res)
          this.isLoading = false
          if (res.success) {
            this.authenticate()
          } else {
            if (res.isBlocked) {
              this.isBlocked = true
              this.isBlockedMsg = 'Votre compte est temporairement bloqué. Veuillez réessayer dans 30 minutes.'
              window.scrollTo(0, 0)
            } else {
              this.errorResponse1 = true
              this.errorResponse2 = true
            }
          }
        })
        .catch((err) => {
          logger.error('Err auth/validateSecretQuestions', err)
          this.isLoading = false
        })
    },
    authenticate () {
      this.isLoading = false
      if (localStorage.getItem('accounts')) {
        this.$router.push('/app/accounts')
      } else {
        this.$router.push('/app/dashboard')
      }
    },
    goBack () {
      Object.assign(this.$data, this.$options.data())
      this.$store.dispatch('auth/getSecretQuestions')
        .then((res) => { this.options.questions1 = res.data })
      this.$store.dispatch('auth/getReverseAuth')
        .then((res) => { this.isReverseAuth = res.isReverseAuth })
    },
  },
}
</script>

<style lang="scss">
@media screen and (max-width: 767px) {
  .ob1-box-radio {
    min-width: 9.5rem;
    width: 50%;
  }
}

.container {
  margin-top: 2rem;
}

@media screen and (min-width: 576px) {
  .container {
    .main > h1 {
      min-width: 20rem;
    }
  }
}

.maz-input--primary.is-focused.maz-input--primary,
.maz-input--primary.is-focused.maz-input--primary:focus,
.maz-input--primary.is-focused.maz-input--primary:hover {
  border-color: #000;
}

.maz-custom {
  padding-top: 1.375rem;

  & > * {
    transition-duration: 0.2s;
  }

  &:focus-within {
    .maz-select.country-selector {
      .maz-input {
        border-bottom: 0.125rem solid;
      }
    }

    .maz-input.input-phone-number {
      border-bottom: 0.125rem solid;
    }
  }

  .maz-select.country-selector {
    .maz-input {
      border: none;
      border-bottom: 0.0625rem solid;
      border-radius: unset;
      .maz-input__icon path.arrow {
        fill: black;
      }
      .maz-input__label {
        color: black;
      }
    }
  }

  .maz-input.input-phone-number {
    border: none;
    border-bottom: 0.0625rem solid;
    border-radius: unset;
    max-width: 18.25rem;

    .maz-input__label {
      color: black;
    }
  }
}

.questions-container {
  max-width: 28rem;
}

.radio-container {
  margin: 0;
  max-width: 28.125rem;

  label {
    margin-left: 0.5rem;
  }
}

.form-group .alert-container {
  max-width: 28.125rem;
}

ul.items-list {
  li.ob1-box-radio:not(:first-child) {
    margin-left: 0.5rem;
  }
}

.ob1-box-radio .item-text:after {
  cursor: pointer;
}
</style>
